import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Pencil, Check, X, ShieldCheck, ShieldAlert, Building2, Clock, ExternalLink } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";

const STALE_DAYS = 90;
const officeFields = (mp) => ({ honorific: mp.honorific || "", office_address_1: mp.office_address_1 || "", office_address_2: mp.office_address_2 || "", office_suburb: mp.office_suburb || "", office_state: mp.office_state || "", office_postcode: mp.office_postcode || "", office_phone: mp.office_phone || "", ministerial_title: mp.ministerial_title || "" });
const officeStatus = (mp) => {
  if (!mp.office_verified_at) return "unverified";
  return Date.now() - new Date(mp.office_verified_at).getTime() > STALE_DAYS * 86400000 ? "stale" : "verified";
};

const Row = ({ mp }) => {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ name: mp.name, party: mp.party, email: mp.email, ...officeFields(mp) });
  const [busy, setBusy] = useState(false);

  const save = async (extra = {}, fromForm = true) => {
    setBusy(true);
    try {
      await api.put(`/admin/mps/${encodeURIComponent(mp.electorate)}`, { ...(fromForm ? form : {}), ...extra });
      toast.success(`${mp.electorate} updated`);
      qc.invalidateQueries({ queryKey: ["admin-mps"] });
      qc.invalidateQueries({ queryKey: ["electorate", mp.electorate] });
      setEdit(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const cell = "bg-[#121E2B] border-[#2C3E55] h-8 text-xs";
  const f = (k, ph, cls = "") => <Input data-testid={`admin-mp-${k}-input`} className={`${cell} ${cls}`} value={form[k]} placeholder={ph} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />;
  const status = officeStatus(mp);
  const office = [mp.office_address_1, mp.office_address_2, [mp.office_suburb, mp.office_state, mp.office_postcode].filter(Boolean).join(" ")].filter(Boolean);
  const statusCls = { verified: "border-[#8FA58A] text-[#8FA58A]", stale: "border-[#E8C870] text-[#E8C870]", unverified: "border-[#E2705A]/60 text-[#E2705A]" }[status];

  return (
    <tr data-testid={`admin-mp-row-${mp.electorate}`} className="border-t border-[#2C3E55]/60 hover:bg-[#121E2B]/60 align-top">
      <td className="px-4 py-2 font-semibold whitespace-nowrap">{mp.electorate}</td>
      <td className="px-4 py-2 min-w-[180px]">
        {edit ? <div className="flex gap-1">{f("honorific", "Hon", "w-16")}{f("name", "Name")}</div> : <span>{[mp.honorific, mp.name].filter(Boolean).join(" ")}{mp.ministerial_title && <span className="block text-[11px] text-[#F8FAFC]/50">{mp.ministerial_title}</span>}</span>}
        {edit && f("ministerial_title", "Ministerial title (optional)", "mt-1")}
      </td>
      <td className="px-4 py-2">{edit ? f("party", "Party") : <span className="text-[#F8FAFC]/75">{mp.party}</span>}</td>
      <td className="px-4 py-2 min-w-[200px]">
        {edit ? f("email", "Email") : <span className="text-[#8FA58A] text-xs break-all">{mp.email || <em className="text-[#F8FAFC]/40">none</em>}</span>}
        <button type="button" data-testid="admin-mp-verify-toggle" onClick={() => save({ email_verified: !mp.email_verified }, false)} disabled={busy} title={mp.email_verified ? "Marked verified — click to unmark" : "Assumed from APH naming pattern — click once you've checked it"} className={`mt-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${mp.email_verified ? "border-[#8FA58A] text-[#8FA58A]" : "border-[#E2705A]/60 text-[#E2705A]"}`}>
          {mp.email_verified ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />} {mp.email_verified ? "Email verified" : "Email assumed"}
        </button>
      </td>
      <td className="px-4 py-2 min-w-[240px]" data-testid={`admin-mp-office-${mp.electorate}`}>
        {edit ? (
          <div className="space-y-1">
            {f("office_address_1", "Address line 1")}{f("office_address_2", "Address line 2")}
            <div className="flex gap-1">{f("office_suburb", "Suburb")}{f("office_state", "State", "w-16")}{f("office_postcode", "Postcode", "w-20")}</div>
            {f("office_phone", "Phone")}
          </div>
        ) : (
          <div className="text-xs text-[#F8FAFC]/75 leading-snug">
            {office.length ? office.map((l) => <div key={l}>{l}</div>) : <em className="text-[#E2705A]">No electorate office on file</em>}
            {mp.office_phone && <div className="text-[#F8FAFC]/50">{mp.office_phone}</div>}
          </div>
        )}
        <button type="button" data-testid="admin-mp-office-verify-toggle" onClick={() => save({ office_verified: status === "unverified" || status === "stale" }, false)} disabled={busy} title={status === "verified" ? `Verified ${new Date(mp.office_verified_at).toLocaleDateString("en-AU")} — click to unmark` : status === "stale" ? "Verified more than 90 days ago — re-check and click to refresh" : "From the APH list — click once you've checked it"} className={`mt-1.5 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${statusCls}`}>
          {status === "verified" ? <Building2 className="w-3 h-3" /> : status === "stale" ? <Clock className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />} {status === "verified" ? `Office verified ${new Date(mp.office_verified_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}` : status === "stale" ? "Office check overdue" : "Office unverified"}
        </button>
      </td>
      <td className="px-4 py-2 text-right whitespace-nowrap">
        <a href={`https://www.aph.gov.au/Senators_and_Members/Parliamentarian_Search_Results?q=${encodeURIComponent(mp.surname || mp.name.split(" ").slice(-1)[0])}&mem=1&par=-1&gen=0&ps=12`} target="_blank" rel="noopener noreferrer" data-testid={`admin-mp-aph-link-${mp.electorate}`} title="Open this member on aph.gov.au to check the office details" className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-[11px] text-[#8FA58A] hover:text-[#F8FAFC] hover:bg-[#8FA58A]/10"><ExternalLink className="w-3.5 h-3.5" /> APH</a>
        {edit ? (
          <>
            <button data-testid="admin-mp-save-button" onClick={() => save()} disabled={busy} className="p-2 rounded-lg text-[#8FA58A] hover:bg-[#8FA58A]/10"><Check className="w-4 h-4" /></button>
            <button data-testid="admin-mp-cancel-button" onClick={() => { setEdit(false); setForm({ name: mp.name, party: mp.party, email: mp.email, ...officeFields(mp) }); }} className="p-2 rounded-lg text-[#F8FAFC]/60 hover:bg-[#2C3E55]"><X className="w-4 h-4" /></button>
          </>
        ) : (
          <button data-testid="admin-mp-edit-button" onClick={() => setEdit(true)} className="p-2 rounded-lg text-[#F8FAFC]/60 hover:text-[#E2705A] hover:bg-[#E2705A]/10"><Pencil className="w-4 h-4" /></button>
        )}
      </td>
    </tr>
  );
};

export const MPsManager = () => {
  const { data: mps = [] } = useQuery({ queryKey: ["admin-mps"], queryFn: () => api.get("/admin/mps").then((r) => r.data) });
  const [q, setQ] = useState("");
  const [onlyStale, setOnlyStale] = useState(false);
  const s = q.toLowerCase();
  const rows = mps.filter((m) => (!s || m.electorate.toLowerCase().includes(s) || m.name.toLowerCase().includes(s) || m.party.toLowerCase().includes(s)) && (!onlyStale || officeStatus(m) !== "verified"));
  const verified = mps.filter((m) => m.email_verified).length;
  const officeOk = mps.filter((m) => officeStatus(m) === "verified").length;
  const stale = mps.length - officeOk;

  return (
    <div className="rounded-2xl bg-[#1B2A41] border border-[#2C3E55] overflow-hidden">
      <div className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center border-b border-[#2C3E55]">
        <div className="flex items-center gap-2 flex-1 bg-[#121E2B] rounded-full px-4 h-10 border border-[#2C3E55]">
          <Search className="w-4 h-4 text-[#8FA58A]" />
          <input data-testid="admin-mps-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search electorate, member or party" className="flex-1 bg-transparent outline-none text-sm" />
        </div>
        <button type="button" onClick={() => setOnlyStale((v) => !v)} data-testid="admin-mps-stale-filter" className={`h-9 px-3 rounded-full text-xs border ${onlyStale ? "bg-[#E8C870]/20 border-[#E8C870] text-[#E8C870]" : "border-[#2C3E55] text-[#F8FAFC]/70"}`}>
          {stale} office{stale === 1 ? "" : "s"} need{stale === 1 ? "s" : ""} checking
        </button>
        <p className="text-xs text-[#F8FAFC]/60" data-testid="admin-mps-verified-count">{verified}/{mps.length} emails verified · {officeOk}/{mps.length} offices verified</p>
      </div>
      <p className="px-4 py-3 text-xs text-[#F8FAFC]/60 border-b border-[#2C3E55]">
        Sitting members from the 48th Parliament. Electorate office addresses come from the Parliament of Australia members list (not Parliament House) and feed the F.E.R.M letter. Click <span className="text-[#E2705A]">Office unverified</span> once you've checked a row on aph.gov.au — verified rows go <span className="text-[#E8C870]">overdue</span> after {STALE_DAYS} days.
      </p>
      <div className="overflow-x-auto">
        <table data-testid="admin-mps-table" className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-[#8FA58A] bg-[#121E2B]">
            <tr>{["Electorate", "Sitting member", "Party", "Email", "Electorate office", ""].map((h) => <th key={h} className="px-4 py-3 font-type">{h}</th>)}</tr>
          </thead>
          <tbody>{rows.map((m) => <Row key={m.electorate} mp={m} />)}</tbody>
        </table>
      </div>
    </div>
  );
};
